import imageCompression from "browser-image-compression";

// Formats we must not rasterise / re-encode: vectors and animations.
const PASSTHROUGH = new Set(["image/svg+xml", "image/gif"]);
// Below this, compression isn't worth the CPU / worker spin-up.
const MIN_COMPRESS_BYTES = 100 * 1024;

const EXT_FOR_TYPE = { "image/jpeg": ".jpg", "image/png": ".png", "image/webp": ".webp" };

/**
 * Shrink a user-picked image before upload so raw 5–10 MB phone photos don't
 * land on disk at full size. Non-images (PDF), vectors (SVG) and animations
 * (GIF) pass through untouched, and on any failure the original File is
 * returned — an upload must never break because optimisation failed.
 *
 * @param {File} file
 * @param {object} [options] extra `browser-image-compression` options (override defaults)
 * @returns {Promise<File>}
 */
export async function compressImage(file, options = {}) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (PASSTHROUGH.has(file.type)) return file;
  if (file.size <= MIN_COMPRESS_BYTES) return file;

  const opts = {
    maxSizeMB: 0.8, // target under ~800 KB
    maxWidthOrHeight: 1600, // scale down high-res phone photos
    useWebWorker: true,
    fileType: "image/jpeg",
    initialQuality: 0.82,
    ...options,
  };

  try {
    const blob = await imageCompression(file, opts);
    if (blob.size >= file.size) return file; // never hand back something larger
    const ext = EXT_FOR_TYPE[opts.fileType] || ".jpg";
    const base = file.name.replace(/\.[^/.]+$/, "");
    return new File([blob], `${base}${ext}`, { type: opts.fileType, lastModified: Date.now() });
  } catch (err) {
    console.warn("Client image compression failed; uploading original file.", err);
    return file;
  }
}

export default compressImage;
